import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function RenalArteryDopplerPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const aorta = data.aorta || {};
  const rk = data.rightKidney || {};
  const lk = data.leftKidney || {};
  const bl = data.bladder || {};

  const rkVol = (rk.size1 && rk.size2 && rk.size3)
    ? calculateVolume(rk.size1, rk.size2, rk.size3) : null;
  const lkVol = (lk.size1 && lk.size2 && lk.size3)
    ? calculateVolume(lk.size1, lk.size2, lk.size3) : null;

  const measurementsText = [
    aorta.diameter ? `Aorta diameter: ${aorta.diameter} cm` : null,
    aorta.peakSystolicVelocity ? `Aorta PSV: ${aorta.peakSystolicVelocity} cm/s` : null,
    (rk.size1 && rk.size2 && rk.size3)
      ? `Right kidney: ${rk.size1} x ${rk.size2} x ${rk.size3} cm${rkVol ? ` (Vol: ${rkVol} cc)` : ''}` : null,
    rk.peakSystolicVelocity ? `Right renal PSV: ${rk.peakSystolicVelocity} cm/s` : null,
    rk.renalToAortaRatio ? `Right RAR: ${rk.renalToAortaRatio}` : null,
    rk.highestRI ? `Right highest RI: ${rk.highestRI}` : null,
    rk.lowestRI ? `Right lowest RI: ${rk.lowestRI}` : null,
    (lk.size1 && lk.size2 && lk.size3)
      ? `Left kidney: ${lk.size1} x ${lk.size2} x ${lk.size3} cm${lkVol ? ` (Vol: ${lkVol} cc)` : ''}` : null,
    lk.peakSystolicVelocity ? `Left renal PSV: ${lk.peakSystolicVelocity} cm/s` : null,
    lk.renalToAortaRatio ? `Left RAR: ${lk.renalToAortaRatio}` : null,
    lk.highestRI ? `Left highest RI: ${lk.highestRI}` : null,
    lk.lowestRI ? `Left lowest RI: ${lk.lowestRI}` : null,
  ].filter(Boolean).join('\n');

  const isStenosisAbnormal = (v) => v === 'Less than 60% Stenosis' || v === 'Stenosis 60% or greater';

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="RENAL ARTERY DOPPLER ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="DOPPLER MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="AORTA" />
            <PdfLabelValue label="Waveform:" value={aorta.waveform || ''} isAbnormal={aorta.waveform && aorta.waveform !== 'Triphasic/Biphasic'} />

            <PdfAccentSectionHeader title="RIGHT KIDNEY" />
            <PdfLabelValue label="Echotexture:" value={rk.echotexture || ''} isAbnormal={rk.echotexture && rk.echotexture !== 'Normal'} />
            <PdfLabelValue label="Hydronephrosis:" value={rk.hydronephrosis || 'None'} isAbnormal={rk.hydronephrosis && rk.hydronephrosis !== 'None'} />
            <PdfLabelValue label="Estimated stenosis:" value={rk.estimatedStenosis || ''} isAbnormal={isStenosisAbnormal(rk.estimatedStenosis)} />
            <PdfLabelValue label="Renal vein:" value={rk.renalVein || ''} isAbnormal={rk.renalVein && rk.renalVein !== 'Patent'} />
            {rk.other && <PdfLabelValue label="Other:" value={rk.other} isAbnormal />}
            {rk.masses && rk.masses.length > 0 && (
              <PdfLabelValue label="Masses:" value={rk.masses.filter(Boolean).join(', ')} isAbnormal />
            )}

            <PdfAccentSectionHeader title="LEFT KIDNEY" />
            <PdfLabelValue label="Echotexture:" value={lk.echotexture || ''} isAbnormal={lk.echotexture && lk.echotexture !== 'Normal'} />
            <PdfLabelValue label="Hydronephrosis:" value={lk.hydronephrosis || 'None'} isAbnormal={lk.hydronephrosis && lk.hydronephrosis !== 'None'} />
            <PdfLabelValue label="Estimated stenosis:" value={lk.estimatedStenosis || ''} isAbnormal={isStenosisAbnormal(lk.estimatedStenosis)} />
            <PdfLabelValue label="Renal vein:" value={lk.renalVein || ''} isAbnormal={lk.renalVein && lk.renalVein !== 'Patent'} />
            {lk.other && <PdfLabelValue label="Other:" value={lk.other} isAbnormal />}
            {lk.masses && lk.masses.length > 0 && (
              <PdfLabelValue label="Masses:" value={lk.masses.filter(Boolean).join(', ')} isAbnormal />
            )}

            <PdfAccentSectionHeader title="BLADDER" />
            {bl.nwv ? (
              <PdfLabelValue label="Status:" value="Not well visualized" isAbnormal />
            ) : (
              <>
                {bl.noVoid && <PdfLabelValue label="Status:" value="Patient unable to void" forceBlack />}
                <PdfLabelValue label="Right ureteral jet:" value={bl.rightJet ? 'Present' : 'Absent'} isAbnormal={!bl.rightJet} />
                <PdfLabelValue label="Left ureteral jet:" value={bl.leftJet ? 'Present' : 'Absent'} isAbnormal={!bl.leftJet} />
                {bl.other && bl.other.length > 0 && (
                  <PdfLabelValue label="Other:" value={bl.other.filter(Boolean).join(', ')} isAbnormal />
                )}
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
